import { lazy } from 'react';

import { RequireNoSession } from './components/RequireNoSession';
import { RequireNotAnonymous } from './components/RequireNotAnonymous';

import type { RouteObject } from 'react-router-dom';

/**
 * Rotas do módulo de autenticação (v2).
 *
 * Todas ficam **fora** do `AppLayout`: a barra inferior leva às áreas do
 * produto, e oferecê-las no meio do cadastro é convite a abandoná-lo.
 *
 * `lazy` em todas: quem já tem sessão nunca baixa nenhuma dessas telas.
 *
 * O cadastro começa pela **escolha do objetivo** (`/register`) e segue para o
 * formulário daquele objetivo, cada um em rota própria para que o botão voltar
 * do telefone funcione como a pessoa espera. O `?next=` atravessa as rotas
 * preservando a intenção original (plans/plano-autenticacao-v2.md, J1/J2).
 *
 * ## Segmentos literais, e não `/register/:goal`
 *
 * Com parâmetro, `/register/voluntario` (digitado errado, em português) cairia
 * na página em vez de cair no 404, e `finish` teria que ser excluído à mão do
 * parâmetro — um esquecimento ali sombrearia o fim do cadastro do socorro sem
 * conta. Sem parâmetro, também não há valor a validar: quem garante é o
 * roteador.
 *
 * As três rotas de formulário montam o **mesmo** componente, com o objetivo por
 * prop: o que muda entre elas é o título e um campo.
 */

const WelcomePage = lazy(() => import('./routes/Welcome.page'));
const LoginPage = lazy(() => import('./routes/Login.page'));
const ChooseAccountGoalPage = lazy(() => import('./routes/ChooseAccountGoal.page'));
const RegisterAccountPage = lazy(() => import('./routes/RegisterAccount.page'));
const BecomeVolunteerPage = lazy(() => import('./routes/BecomeVolunteer.page'));
const FinishSignUpPage = lazy(() => import('./routes/FinishSignUp.page'));
const PasswordRecoveryPage = lazy(() => import('./routes/PasswordRecovery.page'));
const PasswordRecoverySentPage = lazy(() => import('./routes/PasswordRecoverySent.page'));

export const authRoutes: RouteObject[] = [
  { path: 'welcome', element: <WelcomePage /> },
  // O login e a escolha do objetivo são as duas telas que oferecem "comece uma
  // sessão". Quem já tem uma não vê nenhuma das duas — a guarda explica por que
  // a sessão anônima é exceção.
  {
    element: <RequireNoSession />,
    children: [{ path: 'login', element: <LoginPage /> }],
  },
  // O passo de idade e funções mora fora de `/register` porque não é só de
  // cadastro: metade de quem chega nele já tem conta e está alterando funções
  // (Perfil, vagas, convite de vínculo). A própria tela explica.
  { path: 'volunteer', element: <BecomeVolunteerPage /> },
  {
    path: 'register',
    children: [
      {
        // Tudo que **cria** conta fica atrás da guarda: quem tem conta anônima
        // aberta por um pedido de socorro termina a que já existe em vez de
        // abrir outra (DV-24). `finish` fica de fora de propósito — é o destino
        // dela, e dentro viraria laço de redirecionamento.
        element: <RequireNotAnonymous />,
        children: [
          // A escolha empilha a segunda guarda: anônimo vai para `finish`
          // (acima), e quem já tem conta de verdade vai para a intenção
          // original. Os formulários abaixo **não** entram nela porque o destino
          // deles depende do objetivo — quem resolve é a própria página.
          {
            element: <RequireNoSession />,
            children: [{ index: true, element: <ChooseAccountGoalPage /> }],
          },
          // A ordem é a dos cartões da escolha: do mais comum ao mais raro.
          { path: 'requester', element: <RegisterAccountPage goal="requester" /> },
          { path: 'volunteer', element: <RegisterAccountPage goal="volunteer" /> },
          { path: 'institution', element: <RegisterAccountPage goal="institution" /> },
        ],
      },
      // Destino do link mágico do socorro sem conta (DV-24). Fica sob
      // `/register` porque é cadastro — o que muda é só por onde ele começou.
      { path: 'finish', element: <FinishSignUpPage /> },
    ],
  },
  {
    path: 'password-recovery',
    children: [
      { index: true, element: <PasswordRecoveryPage /> },
      { path: 'sent', element: <PasswordRecoverySentPage /> },
    ],
  },
];

/**
 * Reexportada daqui porque `src/routes/index.tsx` só pode importar o `routes.tsx`
 * de cada módulo — é a exceção declarada na zona de fronteira do ESLint.
 */
export { RequireAppEntry } from './components/RequireAppEntry';
export { RequireSession, type GuestGateRouteCopy } from './components/RequireSession';
export { RequireActiveAccount } from './components/RequireActiveAccount';
